import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Participant,
  ParticipantKind,
  RemoteParticipant,
  Room,
  RoomEvent,
} from 'livekit-client'

export const AGENT_STATUS_TOPIC = 'agent-status'

export type AgentReadinessStatus = 'initializing' | 'ready' | 'reconnecting' | 'failed'

interface AgentStatusMessage {
  type?: string
  status?: AgentReadinessStatus
  ready?: boolean
}

const voiceAgentRoomTypes = new Set(['voiceAssistant', 'voiceAssistant2'])

export function isVoiceAgentRoomType(roomType: string | null | undefined): boolean {
  return Boolean(roomType && voiceAgentRoomTypes.has(roomType))
}

export function isFedorAgentParticipant(participant: Participant | null | undefined): boolean {
  if (!participant) return false

  const identity = participant.identity || ''
  const name = participant.name || ''

  return (
    participant.kind === ParticipantKind.AGENT ||
    participant.isAgent === true ||
    participant.permissions?.agent === true ||
    name === 'Fedor Avatar' ||
    identity.startsWith('agent-') ||
    identity.startsWith('avatar-') ||
    identity.startsWith('fedor-') ||
    identity.startsWith('fedor-page-')
  )
}

function getParticipantStatus(participant: Participant): AgentReadinessStatus | null {
  const attributes = participant.attributes || {}

  if (attributes.agentStatus === 'ready' || attributes.agentReady === 'true') {
    return 'ready'
  }

  if (
    attributes.agentStatus === 'initializing' ||
    attributes.agentStatus === 'reconnecting' ||
    attributes.agentStatus === 'failed'
  ) {
    return attributes.agentStatus
  }

  return null
}

function parseAgentStatusPayload(payload: Uint8Array): AgentReadinessStatus | null {
  try {
    const parsed = JSON.parse(new TextDecoder('utf-8').decode(payload)) as AgentStatusMessage

    if (parsed.type !== 'agent_status') {
      return null
    }

    if (parsed.ready === true) {
      return 'ready'
    }

    if (
      parsed.status === 'initializing' ||
      parsed.status === 'ready' ||
      parsed.status === 'reconnecting' ||
      parsed.status === 'failed'
    ) {
      return parsed.status
    }
  } catch (error) {
    console.error('[useAgentReadiness] Error parsing agent status payload:', error)
  }

  return null
}

function readRoomAgentStatus(room: Room | null, isVoiceRoom: boolean): AgentReadinessStatus {
  if (!isVoiceRoom) {
    return 'ready'
  }

  if (!room) {
    return 'initializing'
  }

  const statuses = Array.from(room.remoteParticipants.values())
    .filter(isFedorAgentParticipant)
    .map(getParticipantStatus)
    .filter(Boolean) as AgentReadinessStatus[]

  if (statuses.includes('ready')) return 'ready'
  if (statuses.includes('failed')) return 'failed'
  if (statuses.includes('reconnecting')) return 'reconnecting'

  return 'initializing'
}

export function useAgentReadiness(room: Room | null, roomType: string | null | undefined) {
  const isVoiceRoom = useMemo(() => isVoiceAgentRoomType(roomType), [roomType])
  const [agentStatus, setAgentStatus] = useState<AgentReadinessStatus>(() =>
    readRoomAgentStatus(room, isVoiceRoom),
  )

  const syncStatusFromRoom = useCallback(() => {
    setAgentStatus(readRoomAgentStatus(room, isVoiceRoom))
  }, [isVoiceRoom, room])

  useEffect(() => {
    syncStatusFromRoom()
  }, [syncStatusFromRoom])

  useEffect(() => {
    if (!room || !isVoiceRoom) {
      return undefined
    }

    const handleParticipantChanged = () => {
      syncStatusFromRoom()
    }

    const handleParticipantAttributesChanged = (
      _changedAttributes: Record<string, string>,
      participant: Participant,
    ) => {
      if (isFedorAgentParticipant(participant)) {
        syncStatusFromRoom()
      }
    }

    const handleParticipantPermissionsChanged = (
      _previousPermissions: unknown,
      participant: Participant,
    ) => {
      if (isFedorAgentParticipant(participant)) {
        syncStatusFromRoom()
      }
    }

    const handleDataReceived = (
      payload: Uint8Array,
      participant?: RemoteParticipant,
      _kind?: unknown,
      topic?: string,
    ) => {
      if (topic !== AGENT_STATUS_TOPIC || !isFedorAgentParticipant(participant)) {
        return
      }

      const nextStatus = parseAgentStatusPayload(payload)
      if (nextStatus) {
        setAgentStatus(nextStatus)
      }
    }

    room.on(RoomEvent.ParticipantConnected, handleParticipantChanged)
    room.on(RoomEvent.ParticipantDisconnected, handleParticipantChanged)
    room.on(RoomEvent.ParticipantAttributesChanged, handleParticipantAttributesChanged)
    room.on(RoomEvent.ParticipantPermissionsChanged, handleParticipantPermissionsChanged)
    room.on(RoomEvent.DataReceived, handleDataReceived)

    return () => {
      room.off(RoomEvent.ParticipantConnected, handleParticipantChanged)
      room.off(RoomEvent.ParticipantDisconnected, handleParticipantChanged)
      room.off(RoomEvent.ParticipantAttributesChanged, handleParticipantAttributesChanged)
      room.off(RoomEvent.ParticipantPermissionsChanged, handleParticipantPermissionsChanged)
      room.off(RoomEvent.DataReceived, handleDataReceived)
    }
  }, [isVoiceRoom, room, syncStatusFromRoom])

  return {
    agentStatus,
    isAgentReady: agentStatus === 'ready',
  }
}
